/**
 * Multisampling (PLAN.md D4): roots every 6 semitones rendered on the device by signalsmith-stretch in an
 * OfflineAudioContext (time-preserving pitch shift, formant compensation when the layer asks for it), plus the
 * pure root-selection math (unit-tested).
 */
import { bakeLoop } from '@tonist/shared';
import { loadStretch, withTimeout } from './stretch';

export interface RootBuffer {
  midi: number;
  buffer: AudioBuffer;
  /** seconds */
  loopStart: number;
  loopEnd: number;
  /** a real recording at this pitch (multisampled source) rather than a rendered transposition */
  real?: boolean;
}

export const ROOT_STEP = 6;
export const MANDALA_LO = 36;
export const MANDALA_HI = 96;
/** Longest loop kept per rendered root (seconds); keeps 7 roots ≈ 16 MB of float PCM. */
/**
 * Longest loop kept per rendered root. Longer loops repeat less audibly; RAM per root ≈ 0.38 MB/s of stereo float,
 * and a drone renders ~8 roots, so 24 s ≈ 74 MB per drone: fine on a desktop, too much for a phone (see rootLoopCap).
 */
export const MAX_ROOT_LOOP_SEC = 24;

/**
 * Vocoder window for a note, in ms. A phase vocoder can only hold a partial steady if its window spans many periods
 * of it; the library's fixed default is roughly eight periods of a middle C and far too short for the bottom of the
 * mandala, where it leaves the partials wandering ±5 dB — heard as warble or digital blipping. Sixteen periods
 * measured as steady as the recording itself (±3 dB, the same as plain resampling) without slowing the render.
 */
export function blockMsFor(hz: number): number {
  const periods = 16;
  return Math.max(80, Math.min(400, (periods * 1000) / Math.max(20, hz)));
}
/** Per-device cap: 6 s on tier-0 devices, 12 s on modest ones, the full cap on desktop-class machines. */
export function rootLoopCap(tier0: boolean): number {
  if (tier0) return 6;
  const nav = typeof navigator !== 'undefined' ? (navigator as Navigator & { deviceMemory?: number }) : undefined;
  const mem = nav?.deviceMemory ?? 0, cores = nav?.hardwareConcurrency ?? 0;
  return mem >= 8 || cores >= 8 ? MAX_ROOT_LOOP_SEC : 12;
}

/**
 * Root MIDI notes to prepare for a sample whose file is at `rootMidi`: every `step` semitones, covering
 * [lo, hi] so that any note in range is within step/2 of a root, and never further than −24..+30 from the file.
 */
export function selectRoots(rootMidi: number, lo = MANDALA_LO, hi = MANDALA_HI, step = ROOT_STEP): number[] {
  const min = Math.max(lo, rootMidi - 24);
  const max = Math.min(hi, rootMidi + 30);
  const roots: number[] = [];
  const half = step / 2;
  let kLo = Math.floor((min - rootMidi + half) / step);
  let kHi = Math.ceil((max - rootMidi - half) / step);
  if (kLo > 0) kLo = 0;
  if (kHi < 0) kHi = 0;
  for (let k = kLo; k <= kHi; k++) roots.push(rootMidi + k * step);
  return roots;
}

/**
 * Nearest root for a (fractional) MIDI note with hysteresis: keep `current` while the note stays within
 * step/2 + hysteresis of it, so a glide hovering on a boundary does not flap between two roots.
 */
export function chooseRoot(roots: readonly number[], midi: number, current?: number, hysteresis = 0.5, step = ROOT_STEP): number {
  if (roots.length === 0) throw new Error('no roots');
  if (current !== undefined && roots.includes(current) && Math.abs(midi - current) <= step / 2 + hysteresis) return current;
  let best = roots[0]!;
  let bestD = Math.abs(midi - best);
  for (const r of roots) {
    const d = Math.abs(midi - r);
    if (d < bestD - 1e-9) { best = r; bestD = d; }
  }
  return best;
}

/**
 * Trim a loop length to the budget while keeping the seam phase-aligned: the largest whole number of
 * fundamental periods that fits in `maxSec` (or the original length when it already fits).
 */
export function fitLoopLength(loopSec: number, periodSec: number, maxSec = MAX_ROOT_LOOP_SEC): number {
  if (loopSec <= maxSec) return loopSec;
  if (!(periodSec > 0) || periodSec > maxSec) return maxSec;
  const periods = Math.floor(maxSec / periodSec);
  return Math.max(periodSec, periods * periodSec);
}

/** Tile the loop region of `channels` so that `frames` frames of continuous input are available. */
export function tileLoop(channels: Float32Array[], loopStart: number, loopEnd: number, frames: number): Float32Array[] {
  const len = Math.max(1, loopEnd - loopStart);
  return channels.map((ch) => {
    const out = new Float32Array(frames);
    for (let i = 0; i < frames; i++) out[i] = ch[loopStart + (i % len)] ?? 0;
    return out;
  });
}

export interface RenderRootOptions {
  /**
   * Analysis window for the phase vocoder, in ms. It has to span several periods of the note being rendered or the
   * partials wander (heard as warble); the library's default is fixed, which is short for the bottom of the mandala.
   */
  blockMs?: number;
  formantLock: boolean;
  rootHz: number;
  /** ms of STFT warm-up discarded from the head of the render */
  warmSec?: number;
  timeoutMs?: number;
  /** cap on the loop length kept for this root (seconds); default MAX_ROOT_LOOP_SEC */
  maxLoopSec?: number;
}

export interface SourceLoop {
  buffer: AudioBuffer;
  loopStart: number; // seconds
  loopEnd: number;   // seconds
}

/**
 * Render one transposed root with signalsmith-stretch offline:
 * 1. tile the (already seamless) loop region so the node sees continuous input,
 * 2. `SignalsmithStretch(offlineCtx)` → `addBuffers` → `start({semitones, formantCompensation, formantBaseHz})`
 *    → `startRendering()`,
 * 3. drop the warm-up, then re-fold the tail into the head with `bakeLoop` (raised-cosine crossfade) because a
 *    phase vocoder's output is not exactly periodic even for periodic input.
 * Loop points are time-preserving (pitch shift, not resample), so the loop stays the same length in seconds.
 */
export async function renderRoot(src: SourceLoop, semitones: number, opts: RenderRootOptions): Promise<{ buffer: AudioBuffer; loopStart: number; loopEnd: number }> {
  const factory = await loadStretch();
  const sr = src.buffer.sampleRate;
  const ch = src.buffer.numberOfChannels;
  const period = 1 / Math.max(20, opts.rootHz * Math.pow(2, semitones / 12));
  const loopSec = fitLoopLength(src.loopEnd - src.loopStart, period, opts.maxLoopSec ?? MAX_ROOT_LOOP_SEC);
  const warm = opts.warmSec ?? 0.6;
  const xfade = Math.min(0.35 * loopSec, 1.5);
  const guard = 0.1;
  const totalSec = warm + xfade + loopSec + guard;
  const frames = Math.ceil(totalSec * sr);

  const lsF = Math.floor(src.loopStart * sr);
  const leF = Math.max(lsF + 1, Math.floor((src.loopStart + loopSec) * sr));
  const chans: Float32Array[] = [];
  for (let c = 0; c < ch; c++) chans.push(src.buffer.getChannelData(c));
  const input = tileLoop(chans, lsF, leF, frames + Math.ceil(0.5 * sr));

  const offline = new OfflineAudioContext(ch, frames, sr);
  const node = await withTimeout(factory(offline, { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [ch] }), opts.timeoutMs ?? 40000, 'stretch node');
  if (opts.blockMs) await node.configure({ blockMs: opts.blockMs });
  node.connect(offline.destination);
  await node.addBuffers(input);
  await node.start({
    active: true,
    input: 0,
    output: 0,
    rate: 1,
    semitones,
    formantCompensation: opts.formantLock,
    formantBaseHz: opts.formantLock ? opts.rootHz : 0,
  });
  const rendered = await withTimeout(offline.startRendering(), opts.timeoutMs ?? 40000, 'offline render');
  try { node.disconnect(); } catch { /* ignore */ }

  const warmF = Math.floor(warm * sr);
  const X = Math.floor(xfade * sr);
  const L = Math.floor(loopSec * sr);
  const G = Math.floor(guard * sr);
  const out: Float32Array[] = [];
  for (let c = 0; c < ch; c++) out.push(rendered.getChannelData(c).slice(warmF, warmF + X + L + G));
  const baked = bakeLoop(out, { start: X, end: X + L, correlation: 1, crossfade: X }, G);
  const buffer = new AudioBuffer({ numberOfChannels: ch, length: baked.channels[0]!.length, sampleRate: sr });
  baked.channels.forEach((data, c) => buffer.copyToChannel(data as Float32Array<ArrayBuffer>, c));
  return { buffer, loopStart: baked.loopStart / sr, loopEnd: baked.loopEnd / sr };
}

/**
 * Merge freshly rendered roots into a voice-shared root array IN PLACE (sorted by midi, de-duplicated) so
 * voices that captured the array pick from the upgraded set at their next root switch.
 */
export function mergeRoots(roots: RootBuffer[], extra: readonly RootBuffer[]): RootBuffer[] {
  for (const r of extra) {
    const i = roots.findIndex((x) => Math.abs(x.midi - r.midi) < 1e-6);
    if (i >= 0) roots[i] = r; else roots.push(r);
  }
  roots.sort((a, b) => a.midi - b.midi);
  return roots;
}

/** Run async jobs with bounded concurrency, preserving order. */
export async function mapLimit<T, R>(items: readonly T[], limit: number, fn: (item: T, i: number) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]!, i);
    }
  });
  await Promise.all(workers);
  return out;
}
